import React, {useCallback, useEffect, useRef, useState} from 'react';
import MainLayout from '../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {Theme} from '../../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {getFontFamily, getHomeTabItemById} from '../../../../utils/helpers';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {getNews} from '../actions';
import {AnalyticsEvents, modalTypes} from '../../../../utils/enums';
import {FilterType} from '../../../../interfaces/reducers/homeState';
import Icon from '../../../../components/icon';
import {tr} from '../../../../translations';
import dayjs, {Dayjs} from 'dayjs';
import {FilterTabNewsProps} from '../../../../interfaces/screens/filterTabNews';
import TabNewsSearch from '../../../../components/tabNewsSearch';
import useChatVoiceRecognition from '../../../../utils/hooks/useChatVoiceRecognition';
import {showFiltersModal} from '../../../../connected-components/filtersModal/actions';
import {openQuestionModal} from '../../../../connected-components/questionModal/actions';
import RecordView from '../../../../components/chat/recordView';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import TabNewsResultsBox from '../../../../components/tabNewsResultsBox';
import GenericButton from '../../../../components/button';
import {Space} from '../../home';
import Animated, {FadeInDown, FadeOutDown} from 'react-native-reanimated';
import {staticRecommendations} from '../../../../assets/static/tabsData';
import Config from 'react-native-config';
import {Platform} from 'react-native';
import '../../../../utils/webAnimations';
import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import {analyticsService} from '../../../../utils/services/analytics';
import screens from '../../../../config/screens';

const Container = styled.View`
  width: 100%;
  height: 100%;
`;
const TabNewsResultsBoxView = styled(TabNewsResultsBox)<{
  responsiveWidth: string;
}>`
  width: ${({responsiveWidth}) => responsiveWidth};
  margin-vertical: ${({theme}) => theme.text.s55}px;
  align-self: center;
`;
const RecommendationButton = styled(GenericButton)<{buttonWidth: string}>`
  width: ${({buttonWidth}) => buttonWidth};
  align-self: center;
  background-color: ${({theme}) => theme.tabNews.cardBackground};
`;

/**
 * TabNewsResult screen component.
 *
 * @param {FilterTabNewsProps} props - The screen component props.
 */
const TabNewsResult: React.FC<any> = ({route}: FilterTabNewsProps) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {hdp, getByScreenSize} = useResponsiveDimensions();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {
    id: tab_id,
    tab_name: tabName,
    filters: prevTabFilters,
    is_recommendation,
    filters_recommendations,
    filters_news_details,
    latest_summary_date,
    last_summary_dates,
    filtersResult: prevFiltersResult,
  } = route?.params || {};
  const {currentUser} = useSelector((state: RootState) => state.auth);
  const {tabs} = useSelector((state: RootState) => state.homeTabs);
  const [search, setSearch] = useState('');
  const [isError, setIsError] = useState(false);
  const [newsCount, setNewsCount] = useState('0');
  const [isLoading, setIsLoading] = useState(true);
  const [filtersResult, setFiltersResult] = useState<any>(
    prevFiltersResult || {},
  );
  const [tabFilters, setTabFilters] = useState<FilterType[]>(
    prevTabFilters || [],
  );
  const [newsFilters, setNewsFilters] = useState<FilterType[]>(
    filters_news_details || [],
  );
  const [recommendationsFilters, setRecommendationsFilters] = useState<
    FilterType[]
  >(filters_recommendations || []);
  const {startListening, stopListening, isRecording} =
    useChatVoiceRecognition(setSearch);
  const [isRecommendation, setIsRecommendation] = useState(is_recommendation);
  const [summaryDateInfo, setSummaryDateInfo] = useState<{
    latestSummaryDate: Dayjs;
    lastSummaryDates: Dayjs[];
  }>({
    latestSummaryDate: latest_summary_date
      ? dayjs(latest_summary_date)
      : dayjs(),
    lastSummaryDates: last_summary_dates
      ? last_summary_dates.map(i => dayjs(i))
      : [],
  });
  const entryTime = useRef(new Date());
  const staticRecommendation =
    staticRecommendations[
      +(tab_id || '') as keyof typeof staticRecommendations
    ];

  const isAppleEmail =
    currentUser?.email?.trim().toLowerCase() === Config.APPLE_EMAIL;

  useFocusEffect(
    useCallback(() => {
      return () => {
        const timeSpent = entryTime
          ? (new Date().getTime() - entryTime?.current?.getTime()) / 1000
          : 0;

        analyticsService?.logEvent(AnalyticsEvents.OpenTab, {
          tabId: tab_id,
          tabName,
          timeSpent,
        });
      };
    }, []),
  );

  const initPage = useCallback(
    async (withoutSearch?: boolean) => {
      setIsError(false);
      setIsLoading(true);
      const res = await getNews({
        tab_id,
        keywords: withoutSearch ? '' : search || '',
        pageSize: 1,
        ...filtersResult,
      });
      setIsLoading(false);
      if (!res) {
        setIsError(true);
        setNewsCount('0');
        return;
      }
      if (
        (search || '').trim() !== '' &&
        res?.data?.length === 0 &&
        !withoutSearch
      ) {
        openQuestionModal({
          customHeaderComponent: () => (
            <Icon
              name={'multitrack-audio'}
              size={theme.text.s1 * 2}
              color={theme.secondary}
            />
          ),
          message: tr('search.empty'),
          type: modalTypes.question,
          confirmText: tr('latest.goToFilters'),
          onConfirm: () => {
            setSearch('');
            initPage(true);
            openFilterModal();
          },
          onReject: () => {
            setSearch('');
            setFiltersResult({});
            initPage(true);
          },
        });
      }
      setNewsCount(res?.total + '' || '0');
    },
    [search, filtersResult],
  );

  const initTabInfo = () => {
    const homeTabItem = getHomeTabItemById(tab_id);
    setNewsFilters(homeTabItem?.filters_news_details || []);
    setRecommendationsFilters(homeTabItem?.filters_recommendations || []);
    setTabFilters(homeTabItem?.filters || []);
    setIsRecommendation(!!homeTabItem?.is_recommendation);
    const lastSummaryDates = (homeTabItem?.last_summary_dates || [])
      .map(date => dayjs(date))
      .sort((a, b) => a.diff(b));
    const latestSummaryDate = homeTabItem?.latest_summary_date
      ? dayjs(homeTabItem?.latest_summary_date)
      : dayjs();
    setSummaryDateInfo({
      latestSummaryDate,
      lastSummaryDates,
    });
  };

  useEffect(() => {
    initTabInfo();
  }, [tabs]);

  useEffect(() => {
    initPage();
  }, [filtersResult, currentUser]);

  const openFilterModal = () => {
    if (tabFilters === null) {
      return;
    }
    showFiltersModal({
      filters: tabFilters || {},
      filtersValues: filtersResult,
      onSubmit: val => setFiltersResult(val),
    });
  };

  const onResetPressHandler = () => {
    setSearch('');
    setFiltersResult({});
  };

  const onViewAllPressHandler = () => {
    if (isError) {
      onResetPressHandler();
      return;
    }
    navigation?.navigate(screens.content.news, {
      filtersResult,
      tabId: tab_id,
      search,
      filters: tabFilters,
      newsFilters,
      summaryDateInfo,
    });
  };

  const onRecommendationPress = () => {
    const navigationParams = staticRecommendation
      ? {staticRecommendation}
      : {
          tabId: tab_id,
          filtersResult,
          isRecommendationByTab: true,
          recommendationsFilters,
          summaryDateInfo,
        };
    navigation?.navigate(screens.content.news, navigationParams);
  };

  return (
    <MainLayout
      overlayColor={theme.homeBackground + '88'}
      backHeader
      menuHeader
      noPadding
      title={tabName}
      noPaddingHeader>
      <RecordView
        contentContainerStyle={{
          paddingBottom: bottomInset + hdp(7),
        }}
        isRecording={isRecording}
        question={search}
        onClose={stopListening}
        onSend={initPage}
      />
      <Container>
        <TabNewsSearch
          search={search}
          setSearch={setSearch}
          title={tabName}
          onSubmitSearch={() => initPage()}
          onSpeakPress={startListening}
          onFilterPress={openFilterModal}
          onResetPress={onResetPressHandler}
        />
        <Animated.View
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in-down'}
            : {entering: FadeInDown, exiting: FadeOutDown})}>
          <TabNewsResultsBoxView
            responsiveWidth={getByScreenSize('94%', '70%')}
            isLoading={isLoading}
            numberOfResults={newsCount}
            onViewAllPress={onViewAllPressHandler}
          />
        </Animated.View>
        {(isRecommendation || !!staticRecommendation) && !isAppleEmail && (
          <Animated.View
            {...(Platform.OS === 'web'
              ? {className: 'screen-fade-in-down'}
              : {entering: FadeInDown.delay(150), exiting: FadeOutDown})}>
            <RecommendationButton
              label={tr('tabNews.recommendationTitle')}
              labelStyle={{
                color: theme.text.dark,
                fontFamily: getFontFamily().bold,
              }}
              onPress={onRecommendationPress}
              buttonWidth={getByScreenSize('94%', '30%')}
              radius={100}
            />
          </Animated.View>
        )}
        <Space bottomInset={bottomInset} extraSpace={hdp(15)} />
      </Container>
    </MainLayout>
  );
};

export default TabNewsResult;
